import { deepCompare } from "@website/utils/deep-compare";
import { memo, useEffect, useRef } from "react";
import { initZin } from "zin_sdk";
import { HotelShortVideosProperties } from "./hotel-short-videos.types";

const Component: React.FC<HotelShortVideosProperties> = ({
  displayType,
  displayId,
  className = "",
  environment = "prod",
  zinKey,
  zinTags
}) => {
  const containerReference = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    initZin({ env: environment, zinKey, zinTags });
  }, [environment, zinKey, zinTags]);

  useEffect(() => {
    const host = containerReference.current;
    if (!host) return;

    let shadowObserver: MutationObserver | null = null;
    let stopped = false;

    const handleShadow = (shadow: ShadowRoot) => {
      const check = () => {
        if (stopped) return;

        const hidden = shadow.querySelector(".placement-container--hidden");
        if (hidden) {
          host.style.display = "none";
          shadowObserver?.disconnect();
          return;
        }

        const visibleContent = shadow.querySelector(
          ".placement-container:not(.placement-container--hidden)"
        );
        if (visibleContent) {
          host.style.minHeight = "auto";
          shadowObserver?.disconnect();
        }
      };

      shadowObserver = new MutationObserver(check);
      shadowObserver.observe(shadow, { childList: true, subtree: true });
      check();
    };

    if (host.shadowRoot) {
      handleShadow(host.shadowRoot);
    } else {
      const hostObserver = new MutationObserver(() => {
        if (host.shadowRoot) {
          hostObserver.disconnect();
          handleShadow(host.shadowRoot);
        }
      });

      hostObserver.observe(host, { childList: true });

      return () => {
        stopped = true;
        hostObserver.disconnect();
        shadowObserver?.disconnect();
      };
    }

    return () => {
      stopped = true;
      shadowObserver?.disconnect();
    };
  }, [zinTags, displayId]);

  const defaultHeight = displayType === "REELS" ? 322 : 142;

  return (
    <div
      ref={containerReference}
      key={zinTags}
      data-placement-type={displayType}
      id={displayId}
      style={{ minHeight: defaultHeight }}
      className={`block overflow-hidden rounded ${className}`}
    />
  );
};

export const HotelShortVideos = memo(Component, deepCompare);
